import { FC } from 'react';

import { Loading } from '@shared/components/Loading';
import { Link } from 'react-router-dom';

import { DeliverModal } from '../components/DeliverModal';
import { PhoneHistoryModal } from '../components/PhoneHistoryModal';
import { RedemptionCouponTable } from '../components/RedemptionCouponTable';
import { RedemptionStatsBar } from '../components/RedemptionStatsBar';
import { ShareHistoryModal } from '../components/ShareHistoryModal';
import { TodayDeliveredTable } from '../components/TodayDeliveredTable';
import { useRedemptionPage } from '../hooks/useRedemptionPage';

export const GiftCouponRedemptionListPage: FC = function GiftCouponRedemptionListPage() {
  const {
    dashboardStatus,
    statistics,
    visibleRequested,
    todayDelivered,
    filteredData,
    productOptions,
    productFilter,
    setProductFilter,
    searchText,
    setSearchText,
    selectedIds,
    isAllSelected,
    handleToggleAll,
    handleToggleSelect,
    setSelectedIds,
    deliveringId,
    isBulkProcessing,
    openDeliverModal,
    openBulkDeliverModal,
    deliverModal,
    setDeliverModal,
    handleConfirmDeliver,
    handleReject,
    handleUpdateRecipientPhone,
    historyModal,
    setHistoryModal,
    openHistoryModal,
    shareHistoryModal,
    setShareHistoryModal,
    openShareHistoryModal,
    showTodayDelivered,
    setShowTodayDelivered,
    toastMessage,
    showToast,
    copyToClipboard,
  } = useRedemptionPage();

  if (dashboardStatus === 'loading' || dashboardStatus === 'idle') {
    return (
      <div className="center-box min-h-screen">
        <Loading />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white px-6 py-4 shadow-sm">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold">발송 대기 쿠폰</h1>
            <p className="mt-1 text-sm text-gray-500">
              교환 신청된 쿠폰을 확인하고 기프티콘을 발송하세요.
            </p>
          </div>
          <Link
            to="/gift-seller/coupon-history"
            className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            쿠폰 내역 검색
          </Link>
        </div>
      </div>

      {/* Stats */}
      <RedemptionStatsBar
        totalPending={visibleRequested.length}
        todayDeliveredCount={todayDelivered.length}
        productStats={productOptions}
        blacklist={statistics?.blacklist ?? []}
        onProductClick={(key) => setProductFilter(key)}
      />

      {/* 오늘 발송 내역 */}
      <TodayDeliveredTable
        todayDelivered={todayDelivered}
        showTodayDelivered={showTodayDelivered}
        setShowTodayDelivered={setShowTodayDelivered}
        copyToClipboard={copyToClipboard}
      />

      {/* Filters */}
      <div className="bg-white px-4 py-3 shadow-sm">
        <div className="flex flex-wrap items-center gap-3">
          <select
            value={productFilter}
            onChange={(e) => setProductFilter(e.target.value)}
            className="max-w-[260px] rounded-lg border px-3 py-2 text-sm"
          >
            <option value="all">전체 상품</option>
            {productOptions.map((opt) => (
              <option key={opt.key} value={opt.key}>
                {opt.label} ({opt.count})
              </option>
            ))}
          </select>

          <input
            type="text"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="쿠폰번호, 전화번호, 주문자번호, 상품명 검색"
            className="flex-1 rounded-lg border px-3 py-2 text-sm"
          />
        </div>

        {selectedIds.size > 0 && (
          <div className="mt-3 flex items-center gap-3 rounded-lg bg-indigo-50 px-4 py-3">
            <span className="text-sm font-medium text-indigo-700">
              {selectedIds.size}건 선택됨
            </span>
            <button
              onClick={openBulkDeliverModal}
              disabled={isBulkProcessing}
              className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isBulkProcessing ? '처리 중...' : '일괄 발송처리'}
            </button>
            <button
              onClick={() => setSelectedIds(new Set())}
              className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              선택 해제
            </button>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="p-4">
        <RedemptionCouponTable
          filteredData={filteredData}
          selectedIds={selectedIds}
          deliveringId={deliveringId}
          productFilter={productFilter}
          onToggleAll={handleToggleAll}
          onToggleSelect={handleToggleSelect}
          onOpenDeliverModal={openDeliverModal}
          onOpenHistoryModal={openHistoryModal}
          onOpenShareHistoryModal={openShareHistoryModal}
          onUpdateRecipientPhone={handleUpdateRecipientPhone}
          isAllSelected={isAllSelected}
          copyToClipboard={copyToClipboard}
          showToast={showToast}
        />
      </div>

      {/* Modals */}
      {deliverModal && (
        <DeliverModal
          deliverModal={deliverModal}
          setDeliverModal={setDeliverModal}
          onConfirm={handleConfirmDeliver}
          onReject={handleReject}
        />
      )}

      {historyModal && (
        <PhoneHistoryModal
          phone={historyModal.phone}
          history={historyModal.history}
          onClose={() => setHistoryModal(null)}
        />
      )}

      {shareHistoryModal && (
        <ShareHistoryModal
          fullCode={shareHistoryModal.fullCode}
          history={shareHistoryModal.history}
          onClose={() => setShareHistoryModal(null)}
        />
      )}

      {/* Toast */}
      {toastMessage && (
        <div className="pointer-events-none fixed inset-x-0 bottom-8 z-[100] flex justify-center">
          <div className="animate-fade-in-out rounded-lg bg-gray-800 px-4 py-2 text-sm font-medium text-white shadow-lg">
            {toastMessage}
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeInOut {
          0% { opacity: 0; transform: translateY(8px); }
          15% { opacity: 1; transform: translateY(0); }
          75% { opacity: 1; transform: translateY(0); }
          100% { opacity: 0; transform: translateY(-4px); }
        }
        .animate-fade-in-out {
          animation: fadeInOut 1.2s ease-in-out forwards;
        }
      `}</style>
    </div>
  );
};
